import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { FileData } from "@scrile/api-provider/dist/api/FileProvider";
import { Review, ReviewSubjectType } from "@scrile/api-provider/dist/api/ReviewProvider";
import { FragmentFields } from "@scrile/api-provider/dist/api/types";
import { AppointmentType } from "@scrile/api-provider/dist/projects/webvideo/AppointmentProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/webvideo/UserPublicDataProvider";
import { useFindByUserName } from "hooks/useFindByUserName";
import usePager from "hooks/usePager";
import { MediaFilesController } from "lib/MediaFileController";
import providers from "lib/providers";
import { LocationState } from "types";
import { Routes } from "../../router/config";

const searchFields: FragmentFields<UserPublicData> = [
  "id",
  "role",
  "username",
  "screenName",
  "disabled",
  "calendlyScheduleUrl",
  { onlineStatus: ["isOnline"] },
  { publicLivechatUser: ["broadcasting"] },
  { avatar: ["urlPart"] },
  { questionary: ["fields", { tags: ["id", "slug", "title"] }] },
];

export interface ProfileVideoData {
  fileData: FileData;
  thumbnailUrl: string;
}

function useController(username: string) {
  const history = useHistory<LocationState>();
  const [appointments, setAppointments] = useState<AppointmentType[]>([]);
  const [video, setVideo] = useState<ProfileVideoData>();
  const [appointmentsLoading, setAppointmentsLoading] = useState(true);
  const [videoLoading, setVideoLoading] = useState(true);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const { user } = useFindByUserName(
    username,
    searchFields,
    useCallback(() => {
      history.push(Routes.root);
    }, [history])
  );

  useEffect(() => {
    (async () => {
      if (user?.id) {
        try {
          setAppointmentsLoading(true);
          const response = await providers.AppointmentProvider.searchAppointmentTypes({ userId: user.id });
          setAppointments(response);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e);
        } finally {
          setAppointmentsLoading(false);
        }
        try {
          setVideoLoading(true);
          const response = await providers.FileProvider.find({
            fileIds: [{ subjectId: user?.id || "", type: "profile_video" }],
          });
          let thumbnailUrl = "";
          if (response.length) thumbnailUrl = MediaFilesController.getVideoThumbnail(response[0].urlPart, "1080p");
          setVideo({ fileData: response[0], thumbnailUrl: thumbnailUrl });
        } finally {
          setVideoLoading(false);
        }
      }
    })();
  }, [user, setAppointments]);

  const { enableScroll, items, getNextPage, getFirstPage, data, setData } = usePager(
    useCallback(
      (page) =>
        providers.ReviewProvider.find({
          data: {
            subjects: [{ subjectId: user?.id || "", subjectType: ReviewSubjectType.USER }],
            sort: "TIME,DESC",
            page,
            size: 6,
          },
        }),
      [user]
    )
  );

  const onReviewAdded = (review: Review) => {
    const localData = items;

    if (!data || items.length === 0) {
      getFirstPage();
    } else {
      if (data.hasNextPage) {
        localData.pop();
      }
      localData.unshift(review);
      setData((prevState) => {
        if (prevState) {
          return {
            ...prevState,
            totalRecords: prevState.totalRecords + 1,
            result: localData,
          };
        }
      });
    }
  };

  useEffect(() => {
    if (user?.id) {
      (async () => {
        setReviewsLoading(true);
        await getFirstPage();
        setReviewsLoading(false);
      })();
    }
  }, [user, getFirstPage]);

  const showBackButton = useMemo(() => history.location.state?.fromPerformers ?? false, [history]);
  const onClickBackButton = useCallback(() => history.goBack(), [history]);

  return {
    user,
    appointments,
    appointmentsLoading,
    video,
    videoLoading,
    reviewsLoading,
    onReviewAdded,
    reviewItems: items,
    getNextReviewsPage: getNextPage,
    enableReviewsScroll: enableScroll,
    totalReviewsRecords: data?.totalRecords.toString(),
    showBackButton,
    onClickBackButton,
  };
}

export default useController;
